import { html } from "lit";
import { customElement, property } from "lit/decorators.js";

import "ui-components/form-elements/input/wow-input";

import { styles } from "./donation-config-panel.css";
import { SignalElement, inject } from "ui-common/di";
import type { ILiveEventFormState, ILiveEventFormService } from "ui-common/services/live-event-form/types";
import { t } from "../../../../translations/i18n";

/**
 * Creator-side config for a donation-tier live event: the fundraising target
 * ("streefbedrag") plus the 5 preset chip amounts shown in the donor modal.
 * Bound to the live-event-form signal service — values are stored in cents
 * but rendered as whole-euro integer inputs, matching the modal's chip UX.
 */
@customElement("donation-config-panel")
export class DonationConfigPanel extends SignalElement {
  static override styles = [styles];

  @property({ type: Boolean }) disabled = false;

  @inject<ILiveEventFormState>("liveEventFormState")
  private state!: ILiveEventFormState;

  @inject<ILiveEventFormService>("liveEventFormService")
  private service!: ILiveEventFormService;

  private onTargetInput = (e: Event) => {
    const v = (e.target as HTMLInputElement).value;
    const cents = v === "" ? null : Math.round(parseFloat(v) * 100);
    this.service.setDonationTarget(cents);
  };

  private onPresetInput(index: number, e: Event) {
    const v = (e.target as HTMLInputElement).value;
    const cents = v === "" ? 0 : Math.round(parseFloat(v) * 100);
    this.service.setDonationPreset(index, cents);
  }

  override render() {
    const target = this.state.donationTargetCents.get();
    const presets = this.state.donationPresetAmounts.get();
    const errors = this.state.validationErrors.get();
    const targetEuros = target === null ? "" : String(target / 100);

    return html`
      <div class="panel">
        <label class="target-field">
          <span class="label">${t("createLiveEventPage.donationTarget")}</span>
          <wow-input
            class="target"
            type="number"
            inputmode="decimal"
            prefix="€"
            step="1"
            min="1"
            .value=${targetEuros}
            ?disabled=${this.disabled}
            error-message=${errors.donationTargetCents || ""}
            @input=${this.onTargetInput}
          ></wow-input>
        </label>

        <div>
          <span class="label">${t("createLiveEventPage.donationPresets")}</span>
          <div class="preset-grid">
            ${presets.map(
              (cents, i) => html`
                <wow-input
                  class="preset"
                  type="number"
                  inputmode="decimal"
                  prefix="€"
                  step="1"
                  min="0.50"
                  .value=${String(cents / 100)}
                  ?disabled=${this.disabled}
                  @input=${(e: Event) => this.onPresetInput(i, e)}
                ></wow-input>
              `,
            )}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "donation-config-panel": DonationConfigPanel;
  }
}
